/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { FormEvent, useState } from "react";
import FileUploadSection from "../Common/FileUploadSection/FileUploadSection";
import TextContainer from "../../Container/TextContainer";
import Paper from "@mui/material/Paper";
import ButtonLoading from "../../Button/ButtonLoading";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import _get from "lodash/get";
import _forEach from "lodash/forEach";
import { useNavigate } from "react-router";
import ValidatorTextField from "../../FormFields/ValidatorTextField";
import { isRequired, minStringLength } from "../../../services/validationRules";
import ValidatorSelectField from "../../FormFields/ValidatorSelectField";
import { getCaseLink, getProductData } from "../../../services/productService";
import { useForm } from "../../../provider/Form/FormProvider";
import Typography from "@mui/material/Typography";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../../services/reactQuery/reactQueryService";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import { Child } from "../../../types/Entities/Child";
import useMediaObjectCombinationRequest from "../../../hooks/useMediaObjectCombinationRequest";
import LegalbirdLoader from "../../ContentLoader/LegalbirdLoader";
import documentsStyle from "./documentsStyle";
import { Box } from "@mui/material";

type DocumentsProps = {
  product: AbstractCase;
};
const Documents = ({ product }: DocumentsProps) => {
  let mediaObjectCombinationRequestTypes = ["marriageCertificate"];

  if (product.numberChildren > 0) {
    product.children.forEach((child: Child) => mediaObjectCombinationRequestTypes.push("birthCertificateFiles" + child.id));
  }

  const { mediaObjectCombinationRequests, isLoadingMediaObjectCombinationRequest, updateAction, hasAtLeastOneFileOfType } = useMediaObjectCombinationRequest(
    product,
    mediaObjectCombinationRequestTypes
  );

  const [isBlocked, setIsBlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { values, handleSubmit } = useForm();
  const queryClient = useQueryClient();
  const productApiUri = getProductData(product.name, "apiUri");
  const refreshDivorce = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.item(productApiUri, product.id),
    });

  if (isLoadingMediaObjectCombinationRequest) {
    return <LegalbirdLoader />;
  }

  const isComplete = () => {
    if (!hasAtLeastOneFileOfType(mediaObjectCombinationRequests, "marriageCertificate")) {
      return false;
    }

    let completed = true;
    _forEach(product.children, (child, index) => {
      if (!hasAtLeastOneFileOfType(mediaObjectCombinationRequests, "birthCertificateFiles" + child.id)) {
        completed = false;
        return false;
      }

      if (!_get(values, "children[" + index + "].name")) {
        completed = false;
        return false;
      }
      if (!_get(values, "children[" + index + "].livesWith")) {
        completed = false;
        return false;
      }
    });
    return completed;
  };

  const handleSubmitInternal = async (event: FormEvent<HTMLFormElement>) => {
    setIsLoading(true);
    await handleSubmit(event, { preventRedirect: true });
    navigate(getCaseLink(product));
  };

  return (
    <Paper elevation={4}>
      <TextContainer>
        <Box sx={documentsStyle.inlineHintBox}>
          <Typography variant={"h5"} sx={documentsStyle.headline}>
            Wichtig
          </Typography>
          Laden Sie hier Ihr Dokument als Bild oder PDF hoch, oder fotografieren Sie es einfach ab. Bitte beachten Sie dabei, dass alles gut erkennbar und
          nichts abgeschnitten ist. Benutzen Sie bestenfalls beim Fotografieren den Blitz.
        </Box>
        <FileUploadSection
          mediaObjectCombinationRequest={mediaObjectCombinationRequests.marriageCertificate}
          temporaryMediaObjectsFieldname={"marriageCertificateFiles"}
          headline={"Heiratsurkunde"}
          helperText={"Bitte laden Sie hier Ihre Heiratsurkunde hoch. Sollte Ihnen keine deutsche Heiratsurkunde vorliegen, benötigen Sie eine Übersetzung."}
          productEntity={product}
          productEntityId={product.id}
          product={"divorce"}
          updateAction={updateAction}
          setIsBlocked={setIsBlocked}
          isBlocked={isBlocked}
          fieldName={"marriageCertificate"}
          description={"Heiratsurkunde"}
          fileIndex={0}
          withUploadNotification={undefined}
          allowAudioAndVideo={undefined}
        />
        {product.numberChildren > 0 && product.children.length === 1 && (
          <FileUploadSection
            mediaObjectCombinationRequest={mediaObjectCombinationRequests["birthCertificateFiles" + product.children[0].id]}
            headline={"Geburtsurkunde Ihres Kindes"}
            helperText={"Bitte laden Sie hier die Geburtsurkunde Ihres Kindes hoch."}
            productEntity={product.children[0]}
            productEntityId={product.id}
            product={"divorce"}
            updateAction={updateAction}
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            postSubmitCallback={refreshDivorce}
            fieldName={"birthCertificate"}
            description={"Geburtsurkunde"}
            fileIndex={0}
            withUploadNotification={undefined}
            allowAudioAndVideo={undefined}
            temporaryMediaObjectsFieldname={"birthCertificateFiles"}
          />
        )}
        {product.numberChildren > 0 &&
          product.children.length > 1 &&
          product.children.map((child: Child, index: number) => (
            <FileUploadSection
              key={child.id}
              mediaObjectCombinationRequest={mediaObjectCombinationRequests["birthCertificateFiles" + child.id]}
              headline={"Geburtsurkunde Kind " + (index + 1)}
              helperText={"Bitte laden Sie hier die Geburtsurkunde Ihres " + (index + 1) + ". Kindes hoch."}
              productEntity={child}
              productEntityId={product.id}
              product={"divorce"}
              updateAction={updateAction}
              setIsBlocked={setIsBlocked}
              isBlocked={isBlocked}
              postSubmitCallback={refreshDivorce}
              fieldName={"birthCertificate" + index}
              fileIndex={0}
              description={undefined}
              withUploadNotification={undefined}
              allowAudioAndVideo={undefined}
              temporaryMediaObjectsFieldname={"birthCertificateFiles"}
            />
          ))}
        <form onSubmit={handleSubmitInternal}>
          <Grid container spacing={3} sx={{
            justifyContent: "flex-end"
          }}>
            {product.children.map((child: Child, index: number) => {
              const childNumber = index + 1;
              const nameField = "children[" + index + "]" + ".name";
              const livesWithField = "children[" + index + "].livesWith";
              return [
                <Grid
                  key={childNumber}
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <ValidatorTextField
                    name={nameField}
                    label={"Name Kind " + childNumber}
                    validators={[
                      { validator: isRequired, message: "Bitte geben Sie den Namen an" },
                      { validator: minStringLength, params: [2], message: "Bitte kontrollieren Sie Ihre Eingabe" },
                    ]}
                  />
                </Grid>,
                <Grid
                  key={livesWithField}
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <ValidatorSelectField
                    label={"Wohnort Kind " + childNumber}
                    name={livesWithField}
                    validators={[{ validator: isRequired, message: "Bitte geben Sie den Wohnort Ihres Kindes an" }]}
                  >
                    <MenuItem value="petitioner">Lebt bei mir</MenuItem>
                    <MenuItem value="partner">Lebt beim Ehepartner</MenuItem>
                  </ValidatorSelectField>
                </Grid>,
              ];
            })}
          </Grid>
          <br />
          <br />
          <ButtonLoading
            color="primary"
            variant="contained"
            disabled={!isComplete()}
            fullWidth={true}
            type={"submit"}
            isLoading={isLoading}
            loadingText={"Fall wird angelegt"}
          >
            Jetzt abschicken
          </ButtonLoading>
        </form>
      </TextContainer>
    </Paper>
  );
};

export default Documents;
